/**
 * Canonical nursing home claim statuses stored in `nursing_home_claims.status`.
 * Legacy values may still exist in the DB until migrated; use helpers below for checks.
 */
export const NURSING_HOME_CLAIM_STATUS = {
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected",
  /** Awaiting extra detail from the claimant (admin has sent instructions in `notes`) */
  NEEDS_INFO: "needs_info",
} as const;

export type NursingHomeClaimStatus =
  | (typeof NURSING_HOME_CLAIM_STATUS)[keyof typeof NURSING_HOME_CLAIM_STATUS]
  /** @deprecated use PENDING + verification_documents */
  | "pending_review"
  /** @deprecated use PENDING + verification_documents */
  | "pending_call"
  /** optional workflow */
  | "pending_documents";

const PENDING_PREFIX = "pending";

/** True for queued / in-review claims (excludes approved & rejected). */
export function isPendingClaimStatus(status: string | null | undefined): boolean {
  if (!status) return false;
  if (status === NURSING_HOME_CLAIM_STATUS.APPROVED || status === NURSING_HOME_CLAIM_STATUS.REJECTED) {
    return false;
  }
  return status.startsWith(PENDING_PREFIX);
}

export function isRejectedClaimStatus(status: string | null | undefined): boolean {
  return status === NURSING_HOME_CLAIM_STATUS.REJECTED;
}

export function isNeedsInfoClaimStatus(status: string | null | undefined): boolean {
  return status === NURSING_HOME_CLAIM_STATUS.NEEDS_INFO;
}

/** Admin can approve / reject / request more info */
export function canAdminActOnClaim(status: string | null | undefined): boolean {
  return isPendingClaimStatus(status) || isNeedsInfoClaimStatus(status);
}

export function showRequestMoreInfoAction(status: string | null | undefined): boolean {
  return isPendingClaimStatus(status);
}

type VerificationDocs = {
  emailVerified?: boolean;
  role?: string;
  email?: string;
  contactPhone?: string;
  note?: string;
} | null;

/**
 * UI copy for owner dashboard "pending claims" — uses status + verification_documents.
 */
export function getPendingClaimUi(status: string, verification_documents: unknown) {
  if (status === "pending_documents") {
    return {
      label: "Documents required",
      description:
        "We need additional documentation to verify your association with this facility. Check your email for details.",
      tone: "documents" as const,
    };
  }

  const docs = (verification_documents && typeof verification_documents === "object"
    ? verification_documents
    : {}) as VerificationDocs;

  const legacyPhone = status === "pending_call";
  const phoneRequired = docs.emailVerified === false || legacyPhone;

  if (phoneRequired) {
    return {
      label: "Verification needed",
      description:
        "Our team will contact you by phone to confirm your association with this facility. Please keep your contact details up to date.",
      tone: "phone" as const,
    };
  }

  return {
    label: "Under review",
    description:
      "Your claim is being reviewed by our team. We typically respond within 2–3 business days.",
    tone: "review" as const,
  };
}

export function getNeedsInfoClaimUi(notes: string | null | undefined) {
  return {
    label: "Action required",
    description:
      notes?.trim() ||
      "Our team needs more information before we can approve your claim. Check the note below and reply if we’ve contacted you by email.",
    tone: "needs_info" as const,
  };
}
